import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BuildIcon from '@mui/icons-material/Build';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const SUBSCRIPTION_AMOUNT_IRT = 50_000_000;

function App() {
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const openDialog = () => {
        setErrorMsg('');
        setPhone('');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (submitting) return;
        setDialogOpen(false);
    };

    const handleSubmit = async () => {
        setErrorMsg('');
        setSubmitting(true);
        try {
            const res = await fetch('/api/payment/initiate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.url) {
                setErrorMsg(data.error || 'خطا در ایجاد پرداخت');
                setSubmitting(false);
                return;
            }
            window.location.href = data.url;
        } catch (err) {
            setErrorMsg('خطا در ارتباط با سرور. لطفا دوباره تلاش کنید.');
            setSubmitting(false);
        }
    };

    const features = [
        {
            icon: <TelegramIcon sx={{ fontSize: 40 }} />,
            title: 'کانال تلگرام',
            description: '!سوددهی فوق‌العاده بالای اعضا',
            action: 'ثبت‌نام رایگان در کانال اسنایپر',
            link: 'https://t.me/amirambit'
        },
        {
            icon: <BuildIcon sx={{ fontSize: 40 }} />,
            title: 'خدمات مجموعه',
            description: 'ربات هوشمند، اندیکاتور، ابزارها، میم‌کوین‌یاب و ...',
            action: 'مشاهده خدمات',
            link: 'https://t.me/amirambitbot'
        },
        {
            icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
            title: 'ثبت‌نام در صرافی',
            description: 'ثبت‌نام با تخفیف کارمزد در صرافی با امکانات بالا',
            action: 'ثبت‌نام صرافی',
            link: 'https://www.toobit.com/t/amirambit'
        },
        {
            icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
            title: 'ارتباط سریع و آسان',
            description: 'پشتیبانی تلگرام',
            action: 'تماس با پشتیبانی',
            link: 'https://t.me/amirambitcom'
        }
    ];

    return (
        <Box sx={{ flexGrow: 1, direction: 'rtl', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Container maxWidth="md">
                <Box sx={{ pt: 4, pb: 8 }}>
                    <img src="/logo.png" alt="AmiramBit" style={{ width: '200px', height: '200px', margin: '0 auto', display: 'block'}} />
                    <Typography
                        variant="h3"
                        component="h1"
                        align="center"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            mb: 2,
                            mt: 2
                        }}
                    >
                       AmiramBit | امیرم‌بیت
                    </Typography>
                    <Typography variant="h5" component="h2" align="center" gutterBottom sx={{ mb: 4 }}>
                    کاربردی‌ترین آموزش‌های روز بازار
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<ShoppingCartIcon />}
                            onClick={openDialog}
                            sx={{
                                py: 1.5,
                                px: 5,
                                fontSize: '1.1rem',
                                boxShadow: 4,
                                '& .MuiButton-startIcon': { marginLeft: 1, marginRight: -0.5 },
                            }}
                        >
                            خرید اشتراک
                        </Button>
                    </Box>

                    <Stack spacing={3}>
                        {features.map((feature, index) => (
                            <Paper
                                key={index}
                                elevation={2}
                                sx={{
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    '&:hover': {
                                        boxShadow: 6,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                            >
                                <Grid container alignItems="center" spacing={3}>
                                    <Grid item>
                                        <Box sx={{ color: 'primary.main' }}>
                                            {feature.icon}
                                        </Box>
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant="h6" component="h2" gutterBottom>
                                            {feature.title}
                                        </Typography>
                                        <Typography color="text.secondary" paragraph sx={{ mb: 2 }}>
                                            {feature.description}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            href={feature.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {feature.action}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Stack>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 4 }}
                    >
                        در صورتی که لینک بالا کار نکرد، آیدی تلگرام پشتیبانی: @amirambitcom
                    </Typography>
                </Box>
            </Container>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xs" dir="rtl">
                <DialogTitle>خرید اشتراک</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        مبلغ قابل پرداخت: <strong>{SUBSCRIPTION_AMOUNT_IRT.toLocaleString('fa-IR')} تومان</strong>
                        <br />
                        برای تکمیل پرداخت، شماره موبایلی که در ربات بله ثبت کرده‌اید را وارد کنید:
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        type="tel"
                        label="شماره موبایل"
                        placeholder="09123456789"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={submitting}
                        inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
                    />
                    {errorMsg && (
                        <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={submitting}>انصراف</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting || phone.trim() === ''}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {submitting ? 'در حال انتقال...' : 'پرداخت'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default App;
